import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {base, type Cuarentena} from '@/hooks/bateria/use-bateria'
import type {CandidatoIdentidad} from '@/lib/bateria-cola'

/** G14. Solo con el diálogo abierto: cada hoja cuesta una lectura OMR de su pie. */
export function useCuarentenaCandidatos(
  businessId: string,
  evaluacionId: string,
  cuarentenaId: string,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['bateria-cuarentena-candidatos', businessId, evaluacionId, cuarentenaId],
    queryFn: async () =>
      (
        await api.get<CandidatoIdentidad[]>(
          `${base(businessId)}/evaluaciones/${evaluacionId}/cuarentena/${cuarentenaId}/candidatos`
        )
      ).data,
    enabled,
  })
}

const CLAVES_DE_LA_COLA = ['bateria-cuarentena', 'bateria-cuarentena-count', 'bateria-ingestas', 'bateria-evaluacion']

export function useDescartarHojasDeArchivo(businessId: string, evaluacionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (variables: {sourceObjectKey: string; motivo?: string}) =>
      (
        await api.post<{descartadas: number; omitidas: number}>(
          `${base(businessId)}/evaluaciones/${evaluacionId}/cuarentena/descartar`,
          {source_object_key: variables.sourceObjectKey, motivo: variables.motivo ?? null}
        )
      ).data,
    onSuccess: () => {
      for (const clave of CLAVES_DE_LA_COLA) {
        queryClient.invalidateQueries({queryKey: [clave, businessId, evaluacionId]})
      }
    },
  })
}

/** En el destino se leen como una tanda nueva; acá las filas quedan cerradas con motivo y autor. */
export function useMoverHojasDeArchivo(businessId: string, evaluacionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (variables: {sourceObjectKey: string; destinoEvaluacionId: string}) =>
      (
        await api.post<{movidas: number; omitidas: number; job_id: string | null}>(
          `${base(businessId)}/evaluaciones/${evaluacionId}/cuarentena/mover`,
          {source_object_key: variables.sourceObjectKey, destino_evaluacion_id: variables.destinoEvaluacionId}
        )
      ).data,
    onSuccess: (_resultado, variables) => {
      for (const clave of CLAVES_DE_LA_COLA) {
        queryClient.invalidateQueries({queryKey: [clave, businessId, evaluacionId]})
        queryClient.invalidateQueries({queryKey: [clave, businessId, variables.destinoEvaluacionId]})
      }
    },
  })
}

/** La hoja vuelve a `pendiente` y reaparece en la cola de sin ubicar. */
export function useRestaurarCuarentena(businessId: string, evaluacionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (cuarentenaId: string) =>
      (
        await api.post<Cuarentena>(
          `${base(businessId)}/evaluaciones/${evaluacionId}/cuarentena/${cuarentenaId}/restaurar`,
          {}
        )
      ).data,
    onSuccess: () => {
      for (const clave of CLAVES_DE_LA_COLA) {
        queryClient.invalidateQueries({queryKey: [clave, businessId, evaluacionId]})
      }
    },
  })
}
